import * as Menu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";
import { cn } from "../lib/cn";

export const DropdownMenu = Menu.Root;
export const DropdownMenuTrigger = Menu.Trigger;
export const DropdownMenuGroup = Menu.Group;

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof Menu.Content>,
  ComponentPropsWithoutRef<typeof Menu.Content>
>(({ className, align = "end", sideOffset = 6, ...props }, ref) => (
  <Menu.Portal>
    <Menu.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[200px] rounded-md border border-border bg-surface p-1 text-sm text-fg shadow-menu",
        className,
      )}
      {...props}
    />
  </Menu.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

export interface DropdownMenuItemProps extends ComponentPropsWithoutRef<typeof Menu.Item> {
  description?: string;
  danger?: boolean;
}

export const DropdownMenuItem = forwardRef<ElementRef<typeof Menu.Item>, DropdownMenuItemProps>(
  ({ className, description, danger, children, ...props }, ref) => (
    <Menu.Item
      ref={ref}
      className={cn(
        "flex cursor-default flex-col rounded-sm px-2 py-1.5 outline-none select-none data-[highlighted]:bg-surface-inset data-[disabled]:opacity-50",
        danger && "text-err-fg",
        className,
      )}
      {...props}
    >
      <span className="font-medium">{children}</span>
      {description ? <span className="text-xs text-fg-subtle">{description}</span> : null}
    </Menu.Item>
  ),
);
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof Menu.Separator>,
  ComponentPropsWithoutRef<typeof Menu.Separator>
>(({ className, ...props }, ref) => (
  <Menu.Separator ref={ref} className={cn("my-1 h-px bg-border-subtle", className)} {...props} />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export const DropdownMenuLabel = forwardRef<
  ElementRef<typeof Menu.Label>,
  ComponentPropsWithoutRef<typeof Menu.Label>
>(({ className, ...props }, ref) => (
  <Menu.Label
    ref={ref}
    className={cn(
      "px-2 pt-1 pb-0.5 text-2xs font-semibold tracking-wider text-fg-subtle uppercase",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof Menu.CheckboxItem>,
  ComponentPropsWithoutRef<typeof Menu.CheckboxItem>
>(({ className, children, ...props }, ref) => (
  <Menu.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-7 outline-none select-none data-[highlighted]:bg-surface-inset data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <Menu.ItemIndicator className="absolute left-2 flex size-3.5 items-center justify-center rounded-xs bg-accent text-fg-on-accent">
      <Check className="size-2.5" strokeWidth={3} aria-hidden />
    </Menu.ItemIndicator>
    {children}
  </Menu.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

export const DropdownMenuRadioGroup = Menu.RadioGroup;

export const DropdownMenuRadioItem = forwardRef<
  ElementRef<typeof Menu.RadioItem>,
  ComponentPropsWithoutRef<typeof Menu.RadioItem>
>(({ className, children, ...props }, ref) => (
  <Menu.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-7 outline-none select-none data-[highlighted]:bg-surface-inset data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <Menu.ItemIndicator className="absolute left-2.5 size-2 rounded-full bg-accent" />
    {children}
  </Menu.RadioItem>
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";
